import { create } from 'zustand';
import type { PromoBannerKind } from '@danda/shared';
import { apiClient } from '@/lib/api';
import type { PromoBannerDismissals, PromoBannerStatus } from '@/lib/promo-banner-state';

/**
 * State for the web promo-banner sequence.
 *
 * Two concerns, deliberately in one tiny store so the banner survives lobby to
 * game navigation and both layouts share a single hydration:
 *  - server-derived `status` (referral converted? Discord claimed?), fetched via
 *    `getRewardsStatus`;
 *  - per-banner session dismissal, persisted in `sessionStorage` (NOT
 *    localStorage) so a close hides the banner for THIS web session only and it
 *    returns in a new session until the action is completed.
 *
 * Everything is keyed to the active Firebase `uid`: the store is a module
 * singleton that outlives an in-tab account switch, so `setUser` resets status +
 * reloads dismissals whenever the uid changes (otherwise user B would inherit
 * user A's status and hidden banners). In-flight responses are dropped if the
 * uid changed while the fetch was outstanding.
 *
 * The precedence itself is the pure `derivePromoBanner`; this store only holds
 * the inputs. Mirrors the minimal `accountModalStore` pattern.
 */

const DISMISS_KEY_PREFIX = 'promo-banner-dismissed:';

function dismissKey(uid: string, banner: PromoBannerKind): string {
  return `${DISMISS_KEY_PREFIX}${uid}:${banner}`;
}

function readDismissed(uid: string | null): PromoBannerDismissals {
  const read = (banner: PromoBannerKind): boolean => {
    if (!uid) return false;
    try {
      return sessionStorage.getItem(dismissKey(uid, banner)) === '1';
    } catch {
      // sessionStorage blocked (private mode): treat as not dismissed so the
      // banner still shows; worst case it can't be dismissed for the session.
      return false;
    }
  };
  return { invite: read('invite'), discord: read('discord') };
}

interface PromoBannerState {
  /** Active Firebase uid the current status + dismissals belong to. */
  uid: string | null;
  /** Server truth; null until first hydrate resolves. */
  status: PromoBannerStatus | null;
  dismissed: PromoBannerDismissals;
  /** In-flight/completed guard so both layouts don't double-fetch. */
  hydrating: boolean;
  hydrated: boolean;
  /** Set the active user; resets status + reloads dismissals on a uid change. */
  setUser: (uid: string | null) => void;
  /** Fetch status once (no-op if already fetched/in-flight for this uid). */
  hydrate: () => void;
  /** Force a re-fetch (e.g. after the account modal closes and a reward may
   *  have been claimed, or a referral may have converted). */
  refresh: () => void;
  dismiss: (banner: PromoBannerKind) => void;
  /** Cross-user hygiene: clear all state + this uid's dismissal keys. Called
   *  from the sign-out / account-delete funnel alongside the other store
   *  resets so no identity's banner state survives into the next in the tab. */
  reset: () => void;
}

export const usePromoBannerStore = create<PromoBannerState>((set, get) => {
  // Monotonically increasing generation token, bumped on every setUser/reset.
  // A response is applied only if its captured gen still matches, which drops
  // superseded fetches even across an A->null->A same-account switch (uid
  // equality alone would let a stale prior-identity response through).
  let gen = 0;
  // A refresh() requested while a fetch is in flight is deferred, then run once
  // that fetch settles, so a post-claim invalidation is never silently lost.
  let pendingRefresh = false;

  const fetchStatus = () => {
    const myGen = gen;
    set({ hydrating: true });
    apiClient
      .getRewardsStatus()
      .then(({ discord, referralConverted }) => {
        if (myGen !== gen) return; // superseded by a newer identity/reset
        set((s) => ({
          // referralConverted + discordClaimed are irreversible server-side, so
          // never let a later read revert a value already known true. Critically
          // this neutralizes the non-fatal referral-query fallback, which
          // serializes `referralConverted: false` on a read error and would
          // otherwise bounce a converted user back to the invite banner.
          status: {
            referralConverted: referralConverted || (s.status?.referralConverted ?? false),
            discordClaimed: discord === 'credited' || (s.status?.discordClaimed ?? false),
          },
          hydrating: false,
          hydrated: true,
        }));
      })
      .catch(() => {
        if (myGen !== gen) return;
        // Leave status untouched on failure; allow a later retry.
        set({ hydrating: false });
      })
      .finally(() => {
        if (myGen !== gen) return;
        if (pendingRefresh) {
          pendingRefresh = false;
          fetchStatus();
        }
      });
  };

  return {
    uid: null,
    status: null,
    dismissed: { invite: false, discord: false },
    hydrating: false,
    hydrated: false,
    setUser: (uid) => {
      if (uid === get().uid) return;
      gen++;
      pendingRefresh = false;
      set({
        uid,
        status: null,
        dismissed: readDismissed(uid),
        hydrating: false,
        hydrated: false,
      });
    },
    hydrate: () => {
      if (!get().uid || get().hydrating || get().hydrated) return;
      fetchStatus();
    },
    refresh: () => {
      if (!get().uid) return;
      // Queue behind an in-flight fetch instead of dropping the invalidation.
      if (get().hydrating) {
        pendingRefresh = true;
        return;
      }
      fetchStatus();
    },
    dismiss: (banner) => {
      const uid = get().uid;
      if (uid) {
        try {
          sessionStorage.setItem(dismissKey(uid, banner), '1');
        } catch {
          // Non-fatal: dismissal just won't persist across a remount this session.
        }
      }
      set((s) => ({ dismissed: { ...s.dismissed, [banner]: true } }));
    },
    reset: () => {
      gen++;
      pendingRefresh = false;
      const uid = get().uid;
      if (uid) {
        try {
          sessionStorage.removeItem(dismissKey(uid, 'invite'));
          sessionStorage.removeItem(dismissKey(uid, 'discord'));
        } catch {
          // Non-fatal.
        }
      }
      set({
        uid: null,
        status: null,
        dismissed: { invite: false, discord: false },
        hydrating: false,
        hydrated: false,
      });
    },
  };
});
